import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import {
  CLOCK_SKEW_LIMIT_SECONDS,
  createRefreshLoopDetector,
  measureClockSkewSeconds,
  type SessionIssue,
} from "@/lib/sessionHealth";
import SessionHealthAlert from "@/components/auth/SessionHealthAlert";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  /** Set when the session cannot survive on this device (clock skew / refresh loop). */
  sessionIssue: SessionIssue | null;
  dismissSessionIssue: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionIssue, setSessionIssue] = useState<SessionIssue | null>(null);
  const skewRef = useRef<number | null>(null);
  const loopDetector = useRef(createRefreshLoopDetector());

  // Measure the device/server clock difference once. A device clock far from the
  // real time makes every freshly issued token look expired, which drives the
  // endless token-refresh loop that ends in an unexpected sign-out.
  useEffect(() => {
    let cancelled = false;
    const baseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    if (!baseUrl) return;
    measureClockSkewSeconds(baseUrl).then((skew) => {
      if (cancelled || skew === null) return;
      skewRef.current = skew;
      if (Math.abs(skew) > CLOCK_SKEW_LIMIT_SECONDS) {
        console.warn(`[session] device clock differs from server by ${skew}s`);
        setSessionIssue((prev) => prev ?? { kind: "clock_skew", skewSeconds: skew });
      }
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Set up listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (event === "TOKEN_REFRESHED") {
        // Abnormally frequent refreshes mean rotation is looping: stop auto
        // refresh so the session is not revoked, and surface the cause.
        if (loopDetector.current.record()) {
          loopDetector.current.reset();
          supabase.auth.stopAutoRefresh();
          console.warn("[session] token refresh loop detected; auto refresh stopped");
          setSessionIssue({ kind: "refresh_loop", skewSeconds: skewRef.current });
        }
      }
      if (event === "SIGNED_IN" || event === "SIGNED_OUT") loopDetector.current.reset();
      // Only update state if the user identity actually changed.
      // Otherwise TOKEN_REFRESHED on tab focus would create new object references
      // and re-trigger downstream effects (closing open dialogs/forms).
      setSession((prev) => (prev?.access_token === newSession?.access_token ? prev : newSession));
      setUser((prev) => {
        const nextId = newSession?.user?.id ?? null;
        const prevId = prev?.id ?? null;
        if (prevId === nextId) return prev;
        return newSession?.user ?? null;
      });
    });

    // THEN check existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user, session, loading, signOut,
        sessionIssue,
        dismissSessionIssue: () => setSessionIssue(null),
      }}
    >
      {children}
      <SessionHealthAlert />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
